import { useEffect } from 'react';

import {
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Icon,
  Tooltip,
  Box,
} from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import SharedMessage from '../Message/SharedMessage';

import { SharedRadioInputProps } from './RadioInput.types';

const SharedRadioInput = ({
  section,
  label,
  options,
  formStateKey,
  formActions,
  value,
  onChange,
  message,
  required,
  infoText,
  stacked = false,
  disabled,
  subLabel,
  ...rest
}: SharedRadioInputProps) => {
  const dispatch = useAppDispatch();

  const htmlID = subLabel ? `${label} (${subLabel})` : label;

  const isDisabled = useFormBinderDisabled(formStateKey, disabled);

  useEffect(() => {
    if (!required) return;

    if (!value) {
      dispatch(formActions.addMissingMandatoryField({ name: htmlID, section }));
    } else {
      dispatch(formActions.removeMissingMandatoryField(htmlID));
    }
  }, [required, value, htmlID, section, dispatch, formActions]);

  return (
    <FormControl isRequired={required} isDisabled={isDisabled}>
      <RadioGroup value={value} onChange={onChange} {...rest}>
        {stacked ? (
          <Stack direction="column" spacing={2}>
            <Box as={FormLabel} m="0" mb="8px" id={htmlID}>
              {label}
              {infoText && (
                <Tooltip placement="top" label={infoText}>
                  <Icon ml="5px" />
                </Tooltip>
              )}
            </Box>
            <Stack direction="row" spacing={4}>
              {options.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  isDisabled={isDisabled}
                  borderColor={!value && required ? 'red' : 'inherit'}
                  title={isDisabled ? 'Editing is disabled' : ''}
                >
                  {option.label}
                </Radio>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" align="center" spacing={4}>
            <Box as={FormLabel} margin="0" id={htmlID}>
              {label}
              {infoText && (
                <Tooltip placement="top" label={infoText}>
                  <Icon ml="5px" />
                </Tooltip>
              )}
            </Box>

            {options.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                isDisabled={isDisabled}
                borderColor={!value && required ? 'red' : 'inherit'}
                title={isDisabled ? 'Editing is disabled' : ''}
              >
                {option.label}
              </Radio>
            ))}
          </Stack>
        )}
      </RadioGroup>

      {message && (
        <SharedMessage
          {...message}
          section={section}
          formStateKey={formStateKey}
          formActions={formActions}
        />
      )}
    </FormControl>
  );
};

export default SharedRadioInput;
